import { Fragment, useState } from "react";
import { useDispatch } from "react-redux";

import Avatar from "@/design/components/Avatar";
import { showSnackbar } from "@/store/reducers/app";
import Valkyrie, {
  viArrowRightToBracket,
  viArrowsRotateRight,
  viBuilding,
  viCalendar,
  viList,
  viLocationPinDot,
  viMegaphone,
  viMusic,
  viNumpad,
  viPeople,
  viPerson,
  viRectangleP,
  viTableClock,
  viToggleOn,
  viTrashCan,
  viVoicemail,
} from "@sippy-platform/valkyrie";

import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";

function DeleteFlow({ advanced = false, ...props }) {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [pendingCheck, setPendingCheck] = useState(false);
  const [message, setMessage] = useState({});
  const [dialog, setDialog] = useState(false);
  const [check, setCheck] = useState(false);

  const onStart = async () => {
    if (advanced) {
      setLoading(true);

      await Promise.resolve(props.onDeleteCheck())
        .then((response) => {
          setMessage(response.data);
          setDialog(true);
        })
        .finally(() => setLoading(false));
    } else {
      setDialog(true);
    }
  };

  const onRecheck = async () => {
    setPendingCheck(true);

    await Promise.resolve(props.onDeleteCheck()).finally(() => setPendingCheck(false));
  };

  const onCancel = () => {
    setMessage({});
    setDialog(false);
  };

  const onConfirm = async () => {
    setDialog(false);
    setLoading(true);

    await Promise.resolve(props.onDelete())
      .then(() => {
        if (props.onDeleteFinish) props.onDeleteFinish();
        setLoading(false);
        dispatch(showSnackbar(`${props.name} has been removed!`));
      })
      .finally(() => setLoading(false));
  };

  const objectTypes = [
    {
      type: "users",
      name: "Users",
      icon: viPerson,
      url: "call-flow/users",
      primary: "name",
      secondary: "exten",
      id: "id",
    },
    {
      type: "groups",
      name: "Groups",
      icon: viPeople,
      url: "call-flow/groups",
      primary: "name",
      secondary: "exten",
      id: "id",
    },
    {
      type: "inboundRoutes",
      name: "Inbound Routes",
      icon: viArrowRightToBracket,
      url: "call-flow/inbound-routes",
      primary: "name",
      secondary: "did",
      id: "did",
    },
    {
      type: "timetables",
      name: "Timetables",
      icon: viTableClock,
      url: "call-flow/timetable",
      primary: "name",
      id: "id",
    },
    {
      type: "holidaySets",
      name: "Holiday Sets",
      icon: viCalendar,
      url: "call-flow/holidayset",
      primary: "name",
      id: "id",
    },
    {
      type: "voicemails",
      name: "Voicemails",
      icon: viVoicemail,
      url: "call-flow/voicemails",
      primary: "name",
      secondary: "exten",
      id: "id",
    },
    {
      type: "ivrs",
      name: "IVRs",
      icon: viNumpad,
      url: "call-flow/ivrs",
      primary: "name",
      secondary: "exten",
      id: "id",
    },
    {
      type: "queues",
      name: "Queues",
      icon: viList,
      url: "call-flow/queues",
      primary: "name",
      secondary: "exten",
      id: "id",
    },
    {
      type: "customDestinations",
      name: "Custom destinations",
      icon: viLocationPinDot,
      url: "call-flow/destinations",
      primary: "name",
      secondary: "exten",
      id: "id",
    },
    {
      type: "musicOnHolds",
      name: "Music on Hold",
      icon: viMusic,
      url: "media/music-on-hold",
      primary: "name",
      id: "id",
    },
    {
      type: "parkingLots",
      name: "Call Parkings",
      icon: viRectangleP,
      url: "call-flow/call-parkings",
      primary: "name",
      secondary: "exten",
      id: "id",
    },
    {
      type: "intercoms",
      name: "Intercoms",
      icon: viMegaphone,
      url: "call-flow/intercoms",
      primary: "name",
      secondary: "exten",
      id: "id",
    },
    {
      type: "switches",
      name: "Switches",
      icon: viToggleOn,
      url: "call-flow/switches",
      primary: "name",
      secondary: "exten",
      id: "id",
    },
    { type: "pbxes", name: "PBX", icon: viBuilding, url: "pbx", primary: "name" },
  ];

  return (
    <>
      {props.isActionable && (
        <>
          <Button color="inherit" loading={loading} onClick={onStart} sx={{ px: 1.25, fontSize: "1rem" }} id="delete">
            <Valkyrie icon={viTrashCan} />
          </Button>
          <Dialog open={dialog} onClose={onCancel} scroll="paper">
            <Stack
              sx={{
                gap: 2,
              }}
            >
              <DialogTitle sx={{ p: 0 }}>Delete {props.name}</DialogTitle>
              {message !== undefined && Object.keys(message).length >= 1 ? (
                <DialogContent sx={{ p: 0 }}>
                  <Typography>
                    <b>{props.name}</b> is still in use in the following resources. You can resolve these conflicts or
                    delete all occurrences automatically.
                  </Typography>
                  <List component="div" sx={{ mx: 0, mt: 1 }}>
                    {objectTypes.map((objectType) => {
                      if (message.occurrences[objectType.type]?.length > 0) {
                        return (
                          <Fragment key={objectType.name}>
                            <ListItem component="div" disablePadding sx={{ minHeight: 40 }}>
                              <ListItemText
                                primary={
                                  <Typography variant="h6" sx={{ m: 0 }}>
                                    {objectType.name}
                                  </Typography>
                                }
                                disableTypography
                              />
                            </ListItem>
                            {message.occurrences[objectType.type].map((object, key) => (
                              <ListItem disablePadding sx={{ minHeight: 40 }} key={key}>
                                <ListItemButton
                                  component={Link}
                                  href={`/${objectType.url}${objectType.id ? `/${object[objectType.id]}` : ""}`}
                                  target="_blank"
                                  sx={{ minHeight: 40, px: 1 }}
                                >
                                  <ListItemAvatar sx={{ minWidth: 34 }}>
                                    <Avatar
                                      text={object[objectType.primary]}
                                      icon={objectType.type === "users" ? null : objectType.icon}
                                      px={24}
                                    />
                                  </ListItemAvatar>
                                  <ListItemText
                                    primary={
                                      <>
                                        {object[objectType.primary]}
                                        {!!object[objectType.secondary] && (
                                          <span style={{ opacity: 0.6 }}> &middot; {object[objectType.secondary]}</span>
                                        )}
                                      </>
                                    }
                                  />
                                </ListItemButton>
                              </ListItem>
                            ))}
                          </Fragment>
                        );
                      }
                    })}
                    <Divider variant="middle" sx={{ my: 0.5 }} />
                    <ListItem disablePadding sx={{ minHeight: 40 }}>
                      <ListItemButton onClick={() => setCheck(!check)} sx={{ minHeight: 40, px: 1 }}>
                        <Checkbox
                          checked={check}
                          tabIndex={-1}
                          disableRipple
                          id="check-understand"
                          sx={{ p: 0, mr: 1 }}
                          aria-labelledby="agreement-description"
                        />
                        <ListItemText id="agreement-description" primary="Remove all occurrences automatically." />
                      </ListItemButton>
                    </ListItem>
                  </List>
                </DialogContent>
              ) : (
                <DialogContent sx={{ p: 0 }}>
                  {props.deleteInfo ? (
                    <Typography>{props.deleteInfo}</Typography>
                  ) : (
                    <Typography>
                      <b>{props.name}</b> is not in use and can be removed safely.
                    </Typography>
                  )}
                </DialogContent>
              )}
              <Stack
                direction="row"
                sx={{
                  justifyContent: "flex-end",
                  gap: 1,
                }}
              >
                <Button onClick={() => onCancel()} variant="outlined">
                  Cancel
                </Button>
                <Box sx={{ flexGrow: 1 }} />
                {advanced && (
                  <Button
                    onClick={() => onRecheck()}
                    variant="outlined"
                    startIcon={<Valkyrie icon={viArrowsRotateRight} spin={pendingCheck} />}
                    disabled={loading || pendingCheck}
                  >
                    Recheck
                  </Button>
                )}
                <Button
                  onClick={() => onConfirm()}
                  autoFocus
                  disabled={!!(message && !check && advanced) || pendingCheck || loading}
                  variant="contained"
                  color="error"
                  startIcon={<Valkyrie icon={viTrashCan} />}
                >
                  Delete
                </Button>
              </Stack>
            </Stack>
          </Dialog>
        </>
      )}
    </>
  );
}

DeleteFlow.propTypes = {
  isActionable: PropTypes.bool,
  onDeleteCheck: PropTypes.func,
  onDelete: PropTypes.func,
  onDeleteFinish: PropTypes.func,
  name: PropTypes.string,
  deleteInfo: PropTypes.string,
  advanced: PropTypes.bool,
};

export default DeleteFlow;
